import React, { useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import plaidService from '../services/plaidService';
import Button from './Button';
import styles from './PlaidLink.module.css';

interface PlaidLinkProps {
  onSuccess?: () => void;
}

const PlaidLink: React.FC<PlaidLinkProps> = ({ onSuccess }) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const getLinkToken = async () => {
      try {
        const token = await plaidService.createLinkToken();
        setLinkToken(token);
      } catch (err) {
        setError('Failed to initialize Plaid Link. Please try again.');
        console.error('Error getting link token:', err);
      } finally {
        setLoading(false);
      }
    };

    getLinkToken();
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (public_token, metadata) => {
      try {
        console.log('Exchanging public token:', public_token);
        await plaidService.exchangePublicToken(public_token);
        if (onSuccess) {
          onSuccess();
        }
      } catch (err) {
        setError('Failed to connect bank account. Please try again.');
        console.error('Error exchanging token:', err);
      }
    },
    onExit: (err, metadata) => {
      if (err) {
        // Handle specific Plaid error codes
        switch (err.error_code) {
          case 'INSTITUTION_REGISTRATION_REQUIRED':
            setError('This bank is not yet available. Please try a different bank or contact support.');
            break;
          case 'ITEM_LOGIN_REQUIRED':
            setError('Your bank login has expired. Please reconnect your account.');
            break;
          case 'INVALID_CREDENTIALS':
            setError('Invalid bank credentials. Please check your username and password.');
            break;
          default:
            setError('Failed to connect bank account. Please try again.');
        }
        console.error('Error in Plaid Link:', err);
      }
    },
  });

  if (loading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <p className={styles.error}>{error}</p>
        <Button
          onClick={() => {
            setError(null);
            open();
          }}
          variant="primary"
          fullWidth
        >
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Button
        onClick={() => open()}
        disabled={!ready}
        variant="primary"
        fullWidth
      >
        Connect Bank Account
      </Button>
    </div>
  );
};

export default PlaidLink; 